import React from 'react';
import { FloatButton, Popover, Button, Card } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTourTrigger } from '../../Hooks/useOnboardingTour';
import { getTour } from './tourConfigs';
import { toast } from '../Toast/toast';
import { useSidebar } from '../../contexts/SidebarContext';
import {
  HelpIcon,
  DistributeSpacingVerticalIcon,
  Link04Icon,
  Perspective02Icon,
  RefreshCcw01Icon,
  Settings04Icon
} from '../Icons/iconImages';
import useIsInIframe from '../../Hooks/useIsInIframe';

// Development helper component - only show in development
const TourDebugHelper: React.FC = () => {
    const isInIframe = useIsInIframe();
  const { startManualTour } = useTourTrigger();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobileMenuOpen } = useSidebar();
  const [popoverVisible, setPopoverVisible] = React.useState(false);

  const [iframeSimulationEnabled, setIframeSimulationEnabled] = React.useState(() => {
    const stored = localStorage.getItem('dev_force_iframe');
    return stored === 'true'; // Default to false (disabled)
  });

  // if (process.env.NODE_ENV !== 'development') {
  //   return null;
  // }

  const handleToggleIframeSimulation = (checked: boolean) => {
    setIframeSimulationEnabled(checked);

    if (checked) {
      localStorage.setItem('dev_force_iframe', 'true');
      toast.info('Iframe mode enabled - Reloading...');
    } else {
      localStorage.removeItem('dev_force_iframe');
      toast.info('Iframe mode disabled - Reloading...');
    }

    // Reload to apply iframe detection change
    setTimeout(() => window.location.reload(), 1000);
  };

  const handleResetAllTours = () => {
    localStorage.removeItem('oogwai_onboarding_tours');
    localStorage.removeItem('activeTourId');
    sessionStorage.removeItem('oogwai_first_time_tour_shown'); // Allow first-time tour to show again
    sessionStorage.removeItem('oogwai_pending_tour');
    toast.info('All tours reset! Reloading...');
    setTimeout(() => window.location.reload(), 1000);
  };

  const handleStartTour = (tourId: string) => {
    const tour = getTour(tourId as any);
    if (tour) {
      // Close the popover menu immediately
      setPopoverVisible(false);

      // Check if tour requires a specific path
      if (tour.requiredPath && location.pathname !== tour.requiredPath) {
        // Navigate first, then start tour after a delay to allow page to load
        navigate(tour.requiredPath);
        setTimeout(() => {
          startManualTour(tour);
        }, 1500); // Wait for navigation and page load
      } else {
        // Already on correct page, start tour immediately
        startManualTour(tour);
      }
    }
  };

  // Tour card metadata with icons
  const tourCards = [
    { id: 'dashboard', icon: <DistributeSpacingVerticalIcon />, label: 'Dashboard' },
    { id: 'workspace', icon: <Perspective02Icon />, label: 'Workspaces' },
    { id: 'integrations', icon: <Link04Icon />, label: 'Integrations' },
    { id: 'conversation', icon: <Link04Icon />, label: 'Conversation' },
    { id: 'settings', icon: <Settings04Icon />, label: 'Settings' },
    { id: 'first-time', icon: <RefreshCcw01Icon />, label: 'First Time' },
  ];

  const popoverContent = (
    <div className='max-w-[574px] p-4'>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Product Tour</h2>
        <p className="text-sm text-gray-600">
          Welcome to the {isInIframe ? "Ask AI" : "Oogwai"} product tour. Click on any card to know more about it.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        {tourCards.map((card) => (
          <Card
            key={card.id}
            hoverable
            className="group cursor-pointer border border-[#CAE8DA] transition-all duration-200 hover:shadow-lg hover:border-[#5EC092] hover:bg-[#F5FAF7]"
            onClick={() => handleStartTour(card.id)}
          >
            <div className="flex flex-col gap-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-50 group-hover:bg-[#E6F2EA] transition-colors">
                <div className="w-6 h-6 text-gray-700 group-hover:text-green-600 transition-colors">
                  {card.icon}
                </div>
              </div>
              <span className="text-sm font-medium text-gray-900">{card.label}</span>
            </div>
          </Card>
        ))}
      </div>

      {process.env.NODE_ENV === 'development' && (
        <div className="border-t border-gray-200 pt-4 mb-4 space-y-3">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={iframeSimulationEnabled}
              onChange={(e) => handleToggleIframeSimulation(e.target.checked)}
            />
            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
            <span className="ms-3 text-sm font-medium text-gray-900">Simulate iframe mode (Dev only)</span>
          </label>
        </div>
      )}

      <div className="text-center">
        <Button
          type="link"
          danger
          onClick={handleResetAllTours}
          className="text-sm"
        >
          Reset Tours
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      title={null}
      trigger="click"
      placement="topRight"
      overlayStyle={{ zIndex: 9999 }}
      open={popoverVisible}
      onOpenChange={setPopoverVisible}
    >
      <FloatButton
        className='bottom-4 right-4 z-50 fixed'
        icon={<HelpIcon className="h-[19px] w-[19px] text-gray-800" />}
        tooltip="Product Tour"
        style={{ display: isMobileMenuOpen ? 'none' : 'block' }}
      />
    </Popover>
  );
};

export default TourDebugHelper;
